import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { WebSocketContext } from '../context/WebSocketContext';
import { nullSafeGet } from '../utils/DataAccessUtils';
import {
	getPageEnterOrExitPayload,
	getUpdatedTypingDetails,
	notify,
	notifyGentle,
	shouldShowRealtimeNotePopup,
	WEB_SOCKET_ACTION_TYPES,
	WEB_SOCKET_MESSAGE_TYPES,
} from '../utils/WebSocketUtils';
import throttle from 'lodash/throttle';
import { useNotesBroadcast } from './useNotesBroadcast';

const TYPING_EVENT_INTERVAL = 2000;
const TITLE_BLINKING_INTERVAL = 1000;

interface UseNotesWebSocketProps {
	entityIds: string[];
	currentEntityId: string;
	entityIdName: string;
	entityType: string;
	newNoteEntityType: string;
	refreshMessages: () => void;
	currentUser: any;
}

interface UseNotesWebSocketReturn {
	typingDetails: any[];
	newNoteCount: number;
	resetNewNoteCount: () => void;
	handleTyping: () => void;
	onSendNote: (text?: string, noteType?: string) => void;
}

export function useNotesWebSocket({
	entityIds,
	currentEntityId,
	entityIdName,
	entityType,
	newNoteEntityType,
	refreshMessages,
	currentUser,
}: UseNotesWebSocketProps): UseNotesWebSocketReturn {
	const socketContext = useContext(WebSocketContext) as any;

	const {
		onSendNote,
		onTyping: handleTyping,
		sendWsMessage: sendMessageIfSocketExists,
	} = useNotesBroadcast({
		entityIdName,
		entityId: currentEntityId,
		entityType,
		newNoteEntityType,
		currentUser,
	});

	const [typingDetails, setTypingDetails] = useState<any[]>([]);
	const [newNoteCount, setNewNoteCount] = useState(0);

	// Mutable refs replacing class instance variables
	const previousSocketRef = useRef<any>(null);
	const blinkIntervalRef = useRef<any>(null);
	const originalTitleRef = useRef(document.title);
	const isTabActiveRef = useRef(true);
	const isScrolledToBottomRef = useRef(false);
	const rafIdRef = useRef<any>(null);
	const joinSentForSocketRef = useRef<any>(null);
	const joinInFlightForSocketRef = useRef<any>(null);
	const joinPromiseRef = useRef<Promise<any> | null>(null);
	const cleanupIntervalRef = useRef<any>(null);
	const newNoteCountRef = useRef(0);

	// Keep newNoteCountRef in sync with state for use inside callbacks
	const updateNewNoteCount = useCallback((updater: (prev: number) => number) => {
		setNewNoteCount((prev) => {
			const next = updater(prev);
			newNoteCountRef.current = next;
			return next;
		});
	}, []);

	const resetNewNoteCount = useCallback(() => {
		newNoteCountRef.current = 0;
		setNewNoteCount(0);
	}, []);

	// Refs for values read inside callbacks that intentionally exclude them
	// from dependency arrays. Using direct values would force these into deps,
	// causing attachListener/handleMessage to recreate on every prop change
	// and triggering unnecessary JOIN/LEFT cycles on the WebSocket.
	const entityIdsRef = useRef(entityIds);
	const currentEntityIdRef = useRef(currentEntityId);
	const entityIdNameRef = useRef(entityIdName);
	const entityTypeRef = useRef(entityType);
	const newNoteEntityTypeRef = useRef(newNoteEntityType);
	const refreshMessagesRef = useRef(refreshMessages);

	useEffect(() => {
		entityIdsRef.current = entityIds;
	}, [entityIds]);
	useEffect(() => {
		currentEntityIdRef.current = currentEntityId;
	}, [currentEntityId]);
	useEffect(() => {
		entityIdNameRef.current = entityIdName;
	}, [entityIdName]);
	useEffect(() => {
		entityTypeRef.current = entityType;
	}, [entityType]);
	useEffect(() => {
		newNoteEntityTypeRef.current = newNoteEntityType;
	}, [newNoteEntityType]);
	useEffect(() => {
		refreshMessagesRef.current = refreshMessages;
	}, [refreshMessages]);

	const stopBlinkingTitle = useCallback(() => {
		if (blinkIntervalRef.current) {
			clearInterval(blinkIntervalRef.current);
			blinkIntervalRef.current = null;
		}
		document.title = originalTitleRef.current;
	}, []);

	const startBlinkingTitle = useCallback((message = 'New Notification') => {
		if (isTabActiveRef.current) return;

		// Paused behind the same kill switch as the detail-page toasts: while
		// real-time note pop-ups are paused, the background-tab indicator must
		// stay fully silent — no chime and no title blink. See
		// REALTIME_NOTE_POPUPS_PAUSED in WebSocketUtils.
		if (!shouldShowRealtimeNotePopup()) return;

		const moreThanOneNote = newNoteCountRef.current > 0;
		moreThanOneNote ? notifyGentle() : notify();

		if (blinkIntervalRef.current) return;

		let toggle = false;
		blinkIntervalRef.current = setInterval(() => {
			document.title = toggle ? `${message} 🔔` : originalTitleRef.current;
			toggle = !toggle;
		}, TITLE_BLINKING_INTERVAL);
	}, []);

	const resetJoinState = useCallback(() => {
		joinSentForSocketRef.current = null;
		joinInFlightForSocketRef.current = null;
		joinPromiseRef.current = null;
	}, []);

	const ensurePageEnter = useCallback(() => {
		const ctx = socketContext;
		if (!ctx) return;
		const { socket } = ctx;
		if (!socket) return;

		// Reset if socket changed
		if (joinSentForSocketRef.current !== socket && joinInFlightForSocketRef.current !== socket) {
			resetJoinState();
		}

		if (joinSentForSocketRef.current === socket) return;
		if (joinInFlightForSocketRef.current === socket && joinPromiseRef.current) return;

		joinInFlightForSocketRef.current = socket;

		const joinMessage = getPageEnterOrExitPayload(
			entityIdsRef.current,
			WEB_SOCKET_ACTION_TYPES.JOIN,
			entityTypeRef.current
		);

		const result = sendMessageIfSocketExists(joinMessage);

		joinPromiseRef.current = Promise.resolve(result)
			.then((ok) => {
				if (ok) {
					joinSentForSocketRef.current = socket;
				}
			})
			.catch(() => {})
			.finally(() => {
				if (joinInFlightForSocketRef.current === socket) {
					joinInFlightForSocketRef.current = null;
					joinPromiseRef.current = null;
				}
			});
	}, [socketContext, sendMessageIfSocketExists, resetJoinState]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const throttledJoin = useCallback(
		throttle(() => ensurePageEnter(), TYPING_EVENT_INTERVAL, { leading: true, trailing: false }),
		[ensurePageEnter]
	);

	const updateNewNoteCountOrRefreshMessages = useCallback(() => {
		// Read scroll position synchronously here. Relying solely on the
		// rAF-updated ref leaves a race window where a WS event arriving in
		// the same frame as the user's scroll sees the stale "at bottom"
		// value — causing an unwanted auto-fetch.
		const el = document.getElementById('chatBox');
		const atBottom = el
			? el.scrollTop + el.clientHeight >= el.scrollHeight - 1
			: isScrolledToBottomRef.current;

		if (isTabActiveRef.current && atBottom) {
			refreshMessagesRef.current();
		} else {
			updateNewNoteCount((prev) => prev + 1);
		}
	}, [updateNewNoteCount]);

	const handleMessage = useCallback(
		(event: MessageEvent) => {
			try {
				const data = JSON.parse(event.data);

				if (data[entityIdNameRef.current] != currentEntityIdRef.current) return;

				// Scope guard: when the broadcast carries an `entityType`, only handle
				// messages whose scope matches this listener. Typing events carry
				// `entityType`, while NEW_NOTE broadcasts carry `newNoteEntityType`
				// (e.g. WORK_ORDER_NOTES vs WORK_ORDER), so accept either. Older
				// clients omit `entityType`, so fall through when it's absent for
				// back-compat.
				if (
					data.entityType &&
					data.entityType !== entityTypeRef.current &&
					data.entityType !== newNoteEntityTypeRef.current
				)
					return;

				switch (data.messageType) {
					case WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE:
						startBlinkingTitle('New Note');
						updateNewNoteCountOrRefreshMessages();
						break;

					case WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING:
						setTypingDetails((prev) =>
							getUpdatedTypingDetails(prev, nullSafeGet('typingBy', data))
						);
						break;

					case WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING_STOP:
						setTypingDetails((prev) =>
							prev.filter((detail) => detail.email !== nullSafeGet('typingBy.email', data))
						);
						break;

					default:
						break;
				}
			} catch (err) {
				console.log('JSON parse error', err);
			}
		},
		[startBlinkingTitle, updateNewNoteCountOrRefreshMessages]
	);

	const attachListener = useCallback(() => {
		const ctx = socketContext;
		if (!ctx) return;
		const { socket } = ctx;
		if (!socket) return;

		if (previousSocketRef.current !== socket) {
			if (previousSocketRef.current) {
				previousSocketRef.current.removeEventListener('message', handleMessage);
			}
			socket.removeEventListener('message', handleMessage);
			socket.addEventListener('message', handleMessage);
			previousSocketRef.current = socket;
		}
		throttledJoin();
	}, [socketContext, handleMessage, throttledJoin]);

	const handleVisibilityChange = useCallback(() => {
		isTabActiveRef.current = document.visibilityState === 'visible';
		if (isTabActiveRef.current) {
			stopBlinkingTitle();
		}
	}, [stopBlinkingTitle]);

	const checkScrollPosition = useCallback(() => {
		const el = document.getElementById('chatBox');
		if (el) {
			// chatBox already has paddingBottom equal to the fixed input's height
			// (set in CommonNotesChatPage via the ResizeObserver), so scrollHeight
			// already accounts for the input overlay — a simple "reached the end"
			// check is correct here.
			const scrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
			isScrolledToBottomRef.current = scrolledToBottom;
		}
		rafIdRef.current = requestAnimationFrame(checkScrollPosition);
	}, []);

	// Main lifecycle effect
	useEffect(() => {
		attachListener();
		document.addEventListener('visibilitychange', handleVisibilityChange);

		// Cleanup interval for typing details
		cleanupIntervalRef.current = setInterval(() => {
			setTypingDetails((prev) => getUpdatedTypingDetails(prev, null));
		}, 1000);

		ensurePageEnter();
		checkScrollPosition();

		return () => {
			// Page exit
			const leftMessage = getPageEnterOrExitPayload(
				entityIdsRef.current,
				WEB_SOCKET_ACTION_TYPES.LEFT,
				entityTypeRef.current
			);
			resetJoinState();
			sendMessageIfSocketExists(leftMessage);

			// Remove socket listener from the CURRENT socket (tracked by
			// attachListener), not the stale mount-time socketContext closure.
			if (previousSocketRef.current) {
				previousSocketRef.current.removeEventListener('message', handleMessage);
			}
			previousSocketRef.current = null;

			// Cleanup intervals and timers
			clearInterval(cleanupIntervalRef.current);
			stopBlinkingTitle();
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			cancelAnimationFrame(rafIdRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Re-attach listener when socket changes
	useEffect(() => {
		attachListener();
	}, [attachListener]);

	return {
		typingDetails,
		newNoteCount,
		resetNewNoteCount,
		handleTyping,
		onSendNote,
	};
}
